import { Injectable } from '@angular/core';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { UserQuery } from '@examdojo/user';
import { Observable } from 'rxjs';
import { filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { PayoutRatesHttpService } from './payout-rates-http.service';
import { PayoutRateInsertModel, PayoutRateStoreModel } from './payout-rates.model';
import { PayoutRateStore } from './payout-rates.store';

@Injectable({
  providedIn: 'root',
})
export class PayoutRatesService {
  constructor(
    private readonly httpService: PayoutRatesHttpService,
    private readonly store: PayoutRateStore,
    private readonly userQuery: UserQuery,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  fetchCurrentUserRate(): Observable<PayoutRateStoreModel | null> {
    return this.userQuery.active$.pipe(
      filter(Boolean),
      switchMap((user) => this.httpService.fetchMaybeSingle(user.id)),
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'payout_rate',
        action: 'fetch',
      }),
      tap((rate) => {
        if (rate) {
          this.store.upsert(rate.id, rate);
        }
      }),
    );
  }

  fetchUserRates(userId: string): Observable<PayoutRateStoreModel[]> {
    return this.httpService.fetchUserRateHistory(userId).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'payout_rate',
        action: 'fetch',
      }),
      tap((rates) => this.store.upsertMany(rates)),
    );
  }

  fetchAll(): Observable<PayoutRateStoreModel[]> {
    this.store.setLoading(true);

    return this.httpService.fetchAll().pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'payout_rate',
        action: 'fetch',
      }),
      tap((rates) => this.store.upsertMany(rates)),
      finalize(() => this.store.setLoading(false)),
    );
  }

  updateUserRate(userId: string, rate: number, validFrom?: string): Observable<PayoutRateStoreModel> {
    const updateData: PayoutRateInsertModel = {
      user_id: userId,
      rate,
      ...(validFrom && { valid_from: validFrom }),
    };

    return this.httpService.upsert(updateData).pipe(
      this.errorHandlerService.setHttpErrorMetadata({
        entity: 'payout_rate',
        action: 'update',
      }),
      switchMap((newRate) =>
        this.httpService.fetchAll().pipe(
          tap((allRates) => {
            this.store.set(allRates);
          }),
          map(() => newRate),
        ),
      ),
    );
  }
}
